// ABOUTME: Node autocompletes of the bulk change dialog over one hierarchy, searched by code or list name.
// ABOUTME: NodePicker picks one node (the branch, a link to replace), NodeMultiPicker several (links to add or remove).

import { Autocomplete } from '@ds/coolshop';
import { useMemo, useState } from 'react';

import { NodeOption, useNodesById, useNodeSearch } from '../hooks/useBulkChangeNodes';

export const nodeOptionLabel = (node: Pick<NodeOption, 'code' | 'listName'>) =>
    node.listName && node.listName !== node.code ? `${node.code} · ${node.listName}` : node.code;

/** The options of the search, plus the selected nodes so their chips keep a label. */
function useNodeOptions(hierarchyId: string | null, selectedIds: string[]) {
    const [inputValue, setInputValue] = useState('');
    const [picked, setPicked] = useState<Map<string, NodeOption>>(new Map());
    const { options, loading } = useNodeSearch(hierarchyId, inputValue);
    const missingIds = useMemo(
        () => selectedIds.filter(id => !picked.has(id)),
        [selectedIds, picked],
    );
    const { byId } = useNodesById(hierarchyId ? [hierarchyId] : [], missingIds);

    const nodeOf = (id: string): NodeOption =>
        picked.get(id) ?? byId.get(id) ?? { id, code: id, listName: '' };

    const remember = (nodes: NodeOption[]) =>
        setPicked(previous => {
            const next = new Map(previous);
            nodes.forEach(node => next.set(node.id, node));
            return next;
        });

    return { options, loading, inputValue, setInputValue, nodeOf, remember };
}

type CommonProps = {
    hierarchyId: string | null;
    label: string;
    disabled?: boolean;
    'data-testid'?: string;
};

export function NodePicker({
    hierarchyId,
    label,
    disabled,
    value,
    onChange,
    'data-testid': testId,
}: CommonProps & {
    value: string | null;
    onChange: (id: string | null, node: NodeOption | null) => void;
}) {
    const { options, loading, setInputValue, nodeOf, remember } = useNodeOptions(
        hierarchyId,
        value ? [value] : [],
    );
    return (
        <Autocomplete<NodeOption>
            data-testid={testId}
            options={options}
            value={value ? nodeOf(value) : null}
            loading={loading}
            disabled={disabled || !hierarchyId}
            // The search filters on the server.
            filterOptions={candidates => candidates}
            getOptionLabel={nodeOptionLabel}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            onInputChange={(_event, text, reason) => {
                if (reason === 'input') setInputValue(text);
            }}
            onChange={(_event, node) => {
                if (node) remember([node]);
                onChange(node?.id ?? null, node);
            }}
            label={label}
            fullWidth
        />
    );
}

export function NodeMultiPicker({
    hierarchyId,
    label,
    disabled,
    value,
    onChange,
    'data-testid': testId,
}: CommonProps & {
    value: string[];
    onChange: (ids: string[]) => void;
}) {
    const { options, loading, setInputValue, nodeOf, remember } = useNodeOptions(
        hierarchyId,
        value,
    );
    return (
        <Autocomplete<NodeOption, true>
            data-testid={testId}
            multiple
            options={options}
            value={value.map(nodeOf)}
            loading={loading}
            disabled={disabled || !hierarchyId}
            filterOptions={candidates => candidates}
            getOptionLabel={nodeOptionLabel}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            onInputChange={(_event, text, reason) => {
                if (reason === 'input') setInputValue(text);
            }}
            onChange={(_event, nodes) => {
                remember(nodes);
                onChange(nodes.map(node => node.id));
            }}
            label={label}
            fullWidth
        />
    );
}
